/**
 * `formatFieldValue` dispatcher — extracted from `./index.ts` into its own
 * leaf module so downstream helpers (notably `csvUnit.ts`) can depend on the
 * dispatcher without pulling the whole formatting barrel (and any of its
 * transitively-imported React/PostHog side-effects) into a test setup.
 *
 * `./index.ts` re-exports this module so the public API is unchanged.
 */

import { formatBoolean } from "./boolean";
import { formatCurrent } from "./current";
import { formatDate } from "./date";
import { formatDistance } from "./distance";
import { formatEnergy } from "./energy";
import { formatCurrency, formatNumber, formatPercent } from "./number";
import { formatPhone } from "./phone";
import { formatPower } from "./power";
import { formatResistance } from "./resistance";
import { formatTemperature } from "./temperature";
import { formatText } from "./text";
import type { CustomFormat, FieldFormat, FieldValue, FormattedValue, PercentageFormat } from "./types";
import { formatVoltage } from "./voltage";

/** Internal percentage formatter shared with `./index.ts`. */
const formatPercentage = (value: FieldValue, format: PercentageFormat): FormattedValue => {
  if (value == null) return format.nullText ?? null;
  if (format.hideWhenNull && value === null) return null;

  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return format.nullText ?? null;

  return formatPercent(num, format.decimals ?? 0);
};

/**
 * Main formatting function that routes to the appropriate formatter based on
 * the format type. See `./index.ts` for the fuller doc comment.
 */
export const formatFieldValue = (value: FieldValue, format: FieldFormat): FormattedValue => {
  switch (format.type) {
    case "text":
      return formatText(value, format);
    case "number":
      return formatNumber(value, format);
    case "percentage":
      return formatPercentage(value, format);
    case "currency":
      return formatCurrency(value, format);
    case "date":
      return formatDate(value, format);
    case "boolean":
      return formatBoolean(value, format);
    case "energy":
      return formatEnergy(value, format);
    case "power":
      return formatPower(value, format);
    case "voltage":
      return formatVoltage(value, format);
    case "current":
      return formatCurrent(value, format);
    case "resistance":
      return formatResistance(value, format);
    case "temperature":
      return formatTemperature(value, format);
    case "distance":
      return formatDistance(value, format);
    case "phone":
      return formatPhone(value, format);
    case "custom": {
      const customFormat = format as CustomFormat;
      if (customFormat.hideWhenNull && (value === null || value === undefined)) {
        return null;
      }
      if (value == null) return customFormat.nullText ?? null;
      return customFormat.formatter(value, customFormat.options);
    }
    default:
      // Fallback to string conversion
      return value != null ? String(value) : null;
  }
};
